import React, { useState } from 'react';
import { connect } from 'react-redux';
import useSWR, { mutate } from 'swr';
import moment from 'moment';
import { Translate, translate } from 'react-i18nify';
import Button from '../../../Button';
import Modal from '../../../Modal';
import RenderNewLines from '../../../RenderNewLines';
import CanPerform from '../../../CanPerform';
import ToggleSwitch from '../../../ToggleSwitch';
import api, { basePath } from '../../../../api/apiClient';
import { showNotification } from '../../../../actions';
import styles from './SubscriptionSection.module.scss';

const i18nBase = 'member_details.treatment_areas.subscription_manage';

const STATUS_KEYS: Record<string, string> = {
  PENDING: 'member_details.treatment_areas.subscription_state.pending',
  ACTIVE: 'member_details.treatment_areas.subscription_state.active',
  PAST_DUE: 'member_details.treatment_areas.subscription_state.past_due',
  CANCELLED: 'member_details.treatment_areas.subscription_state.cancelled',
  EXPIRED: 'member_details.treatment_areas.subscription_state.expired'
};

const FREQUENCY_KEYS: Record<string, string> = {
  MONTHLY: `${i18nBase}.frequency_monthly`,
  QUARTERLY: `${i18nBase}.frequency_quarterly`,
  YEARLY: `${i18nBase}.frequency_yearly`
};

const BILLING_MODEL_KEYS: Record<string, string> = {
  RECURRING: `${i18nBase}.billing_model_recurring`,
  INVOICE: `${i18nBase}.billing_model_invoice`
};

const SOURCE_LABELS: Record<string, string> = {
  EXISTING_PATIENT_ROLLOUT: 'Rollout',
  CARE_JOURNEY: 'Vårdresa',
  CAREGIVER_OFFER: 'Vårdgivarerbjudande',
  INVOICE_CONVERSION: 'Fakturakonvertering',
  REACTIVATION: 'Återaktivering',
  EXPIRY_RECOVERY: 'Återhämtning'
};

const PENDING_SOURCE_TEXT: Record<string, string> = {
  CAREGIVER_OFFER: 'Erbjudande skapat av vårdgivare. Väntar på att patienten accepterar.',
  EXISTING_PATIENT_ROLLOUT: 'Patienten har fått erbjudande om att gå över till månadsavgift.',
  CARE_JOURNEY: 'Erbjudande skapat via vårdresa. Väntar på att patienten accepterar.',
  REACTIVATION: 'Återaktiveringserbjudande skapat. Väntar på att patienten accepterar.',
  EXPIRY_RECOVERY: 'Återhämtningserbjudande efter utgången prenumeration.'
};

const ENCOUNTER_REASON_LABELS: Record<string, string> = {
  DOCTOR_APPOINTMENT: 'Läkarbesök',
  NURSE_APPOINTMENT: 'Sjuksköterskebesök',
  HEALTH_ASSESSMENT_STATION: 'Hälsokontroll',
  ERRAND: 'Ärende',
  JOURNAL_SIGNING: 'Journalsignering',
  LAB_SAMPLING: 'Provtagning',
  PHYSICAL_EXAMINATION: 'Fysisk undersökning',
  UNDEFINED: 'Okänd'
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Pågående',
  COMPLETED: 'Betald',
  READY: 'Klar',
  REFUNDED: 'Återbetald',
  FAILED: 'Misslyckad',
  CANCELLED: 'Avbruten',
  EXCLUDED: 'Exkluderad',
  PENDING: 'Väntande',
  SHIPPED: 'Skickad'
};

const VISIBLE_PAYMENTS_DEFAULT = 5;

export const SubscriptionStatusBadge = ({ subscription }: { subscription: Subscription }) => {
  if (subscription.status === 'ACTIVE' && subscription.cancelledAt) {
    return (
      <span className="subscription-status-badge status-pending">
        <Translate value={`${i18nBase}.renewal_cancelled_status`} />
      </span>
    );
  }
  return (
    <span className={`subscription-status-badge status-${subscription.status.toLowerCase()}`}>
      <Translate value={STATUS_KEYS[subscription.status] || subscription.status} />
    </span>
  );
};

const formatAmount = (amount: number | null) => {
  if (amount == null) return '-';
  return `${(amount / 100).toFixed(0)} kr`;
};

const getEncounterPaymentDisplay = (payment: PatientPayment): { label: string; cssClass: string } => {
  const { paymentState, paymentMethod } = payment;

  if (paymentState === 'CREATED' || (paymentState === 'PENDING' && paymentMethod === 'invoice')) {
    return { label: 'Fakturerad', cssClass: 'status-pending' };
  }
  if (paymentState === 'COMPLETE') {
    return { label: 'Betald', cssClass: 'status-active' };
  }
  if (paymentState === 'CANCELLED') {
    return { label: 'Makulerad', cssClass: 'status-cancelled' };
  }
  if (paymentState === 'EXEMPTION') {
    return { label: 'Frikort', cssClass: 'status-info' };
  }
  if (paymentState === 'ERROR') {
    return { label: 'Fel', cssClass: 'status-past_due' };
  }

  const fallbackLabel = PAYMENT_STATUS_LABELS[payment.status] || payment.status;
  return { label: fallbackLabel, cssClass: subscriptionPaymentStatusClass(payment.status) };
};

const subscriptionPaymentStatusClass = (status: string) => {
  switch (status) {
    case 'COMPLETED':
      return 'status-active';
    case 'REFUNDED':
      return 'status-cancelled';
    case 'FAILED':
      return 'status-past_due';
    case 'CANCELLED':
      return 'status-cancelled';
    case 'SHIPPED':
      return 'status-active';
    default:
      return 'status-pending';
  }
};

const formatPaymentSource = (payment: PatientPayment): string => {
  if (payment.source === 'SUBSCRIPTION') return 'Månadsavgift';
  if (payment.source === 'ONE_OFF_PURCHASE') {
    return payment.oneOffPurchaseType === 'BP_MONITOR' ? 'iHealth Ease BP3L + frakt' : 'Okänt';
  }
  const base = payment.encounterName || 'Besök';
  const reasonLabel = payment.encounterReason ? ENCOUNTER_REASON_LABELS[payment.encounterReason] : undefined;
  return reasonLabel ? `${base} (${reasonLabel})` : base;
};

const formatPaymentProvider = (provider: string | undefined): string => {
  if (!provider) return '-';
  if (provider === 'STRIPE') return 'Stripe';
  if (provider === 'billogram') return 'Billogram';
  return provider;
};

const renderPaymentStatus = (payment: PatientPayment) => {
  if (payment.source === 'ENCOUNTER' && payment.paymentState) {
    const { label, cssClass } = getEncounterPaymentDisplay(payment);
    return <span className={`subscription-status-badge ${cssClass}`}>{label}</span>;
  }

  const label = PAYMENT_STATUS_LABELS[payment.status] || payment.status;
  return <span className={`subscription-status-badge ${subscriptionPaymentStatusClass(payment.status)}`}>{label}</span>;
};

type PaymentSettingsData = {
  subscriptionId: number;
  status: string;
  planName: string;
  paymentSettings: string[];
};

const SubscriptionInfoSection = ({
  patient,
  authority,
  authToken,
  showNotification
}: {
  patient: FixMe;
  authority: FixMe;
  authToken: string;
  showNotification: (message: string, type: string) => void;
}) => {
  // A patient has at most one live subscription (it may cover both areas via products), and the
  // monthly fee is a patient-level fact — resolve it by patient so it shows regardless of which
  // care plan it is bound to. The backend orders the live subscription first.
  const { data: subscriptions = [] } = useSWR<Subscription[]>(
    patient?.guid ? `/admin/patient/${patient.guid}/subscriptions` : null
  );

  const [expandedHistoricalId, setExpandedHistoricalId] = useState<number | null>(null);

  // The live subscription (PENDING/ACTIVE/PAST_DUE) is shown in detail; the rest (CANCELLED/
  // EXPIRED) as collapsed history rows.
  const liveSubscription = subscriptions.find(
    (s) => s.status === 'PENDING' || s.status === 'ACTIVE' || s.status === 'PAST_DUE'
  );
  const historicalSubscriptions = subscriptions.filter((s) => s.id !== liveSubscription?.id);

  const paymentSettingsKey = patient?.guid ? `/admin/${patient.guid}/subscription/payment-settings` : null;
  const { data: paymentSettings = [] } = useSWR<PaymentSettingsData[]>(paymentSettingsKey);

  const currentSettings = liveSubscription
    ? paymentSettings.find((ps) => ps.subscriptionId === liveSubscription.id)
    : null;
  const activePaymentMethods = currentSettings?.paymentSettings ?? [];

  const toggleKlarna = async () => {
    if (!liveSubscription || !patient?.guid) return;
    const hasKlarna = activePaymentMethods.includes('KLARNA');
    const newSettings = hasKlarna
      ? activePaymentMethods.filter((s) => s !== 'KLARNA')
      : [...activePaymentMethods, 'KLARNA'];

    try {
      const res = await fetch(
        `${basePath}/admin/${patient.guid}/subscription/${liveSubscription.id}/payment-settings`,
        {
          method: 'PUT',
          headers: { authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentSettings: newSettings })
        }
      );
      if (!res.ok) throw new Error();
      if (paymentSettingsKey) mutate(paymentSettingsKey);
      showNotification(hasKlarna ? 'Klarna inaktiverad' : 'Klarna aktiverad', 'success');
    } catch {
      showNotification('Kunde inte uppdatera betalmetod', 'error');
    }
  };

  return (
    <CanPerform action={authority.manageBilling} fallbackComponent={() => null}>
      <div>
        <h2>Månadsavgift</h2>
        {liveSubscription ? (
          <SubscriptionOverview
            subscription={liveSubscription}
            paymentMethods={activePaymentMethods}
            klarnaEnabled={activePaymentMethods.includes('KLARNA')}
            onToggleKlarna={toggleKlarna}
          />
        ) : (
          <span className="text-muted">Ingen aktiv månadsavgift</span>
        )}
        <SubscriptionHistory
          subscriptions={historicalSubscriptions}
          expandedId={expandedHistoricalId}
          onToggle={(id) => setExpandedHistoricalId(expandedHistoricalId === id ? null : id)}
        />
      </div>
    </CanPerform>
  );
};

const SubscriptionHistory = ({
  subscriptions,
  expandedId,
  onToggle
}: {
  subscriptions: Subscription[];
  expandedId: number | null;
  onToggle: (id: number) => void;
}) => {
  if (!subscriptions.length) {
    return null;
  }
  return (
    <div className={styles.history}>
      <div className={styles.historyHeading}>Historik</div>
      {subscriptions.map((sub) => {
        const isExpanded = expandedId === sub.id;
        return (
          <div key={sub.id}>
            <button type="button" className={styles.historyRow} onClick={() => onToggle(sub.id)}>
              <span>{sub.planName}</span>
              <SubscriptionStatusBadge subscription={sub} />
              <span className={styles.historyDate}>{moment(sub.createdAt).format('YYYY-MM-DD')}</span>
            </button>
            {isExpanded && (
              <div className={styles.historyDetail}>
                <SubscriptionOverview subscription={sub} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const PaymentHistorySection = ({
  patient,
  authority,
  authToken,
  showNotification
}: {
  patient: FixMe;
  authority: FixMe;
  authToken: string;
  showNotification: (message: string, type: string) => void;
}) => {
  const paymentsSWRKey = patient?.guid ? `/admin/patients/${patient.guid}/payments` : null;
  const { data: payments = [] } = useSWR<PatientPayment[]>(paymentsSWRKey);

  return (
    <CanPerform action={authority.manageBilling} fallbackComponent={() => null}>
      <div>
        <h2>Betalningshistorik</h2>
        <PaymentHistory
          payments={payments}
          authToken={authToken}
          patientId={patient?.guid}
          showNotification={showNotification}
          paymentsSWRKey={paymentsSWRKey}
        />
      </div>
    </CanPerform>
  );
};

const PaymentMethodsRow = ({
  methods,
  klarnaEnabled,
  onToggleKlarna
}: {
  methods: string[];
  klarnaEnabled: boolean;
  onToggleKlarna: () => void;
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginTop: '12px',
      paddingTop: '12px',
      borderTop: '1px solid var(--grey-200)'
    }}
  >
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
      <span style={{ fontSize: 'var(--font-12)', color: 'var(--grey-500)' }}>Betalmetoder:</span>
      {methods.includes('CARD') && (
        <svg width="36" height="24" viewBox="0 0 36 24" fill="none">
          <rect x="0.5" y="0.5" width="35" height="23" rx="3" fill="#1A3B6B" stroke="#0F2647" />
          <rect x="4" y="5" width="7" height="5" rx="1" fill="#C8A951" />
          <line x1="4" y1="7.5" x2="11" y2="7.5" stroke="#B8963D" strokeWidth="0.5" />
          <line x1="7.5" y1="5" x2="7.5" y2="10" stroke="#B8963D" strokeWidth="0.5" />
          <circle cx="8" cy="17" r="1" fill="#6B8DB5" />
          <circle cx="12" cy="17" r="1" fill="#6B8DB5" />
          <circle cx="16" cy="17" r="1" fill="#6B8DB5" />
          <circle cx="20" cy="17" r="1" fill="#6B8DB5" />
          <text x="28" y="19" textAnchor="middle" fontSize="5" fill="#8BAFD4" fontFamily="sans-serif">
            KORT
          </text>
        </svg>
      )}
      {methods.includes('KLARNA') && (
        <svg width="36" height="24" viewBox="0 0 36 24" fill="none">
          <rect width="36" height="24" rx="4" fill="#FFB3C7" />
          <text x="18" y="16" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#0A0B09" fontFamily="sans-serif">
            Klarna
          </text>
        </svg>
      )}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
      <span style={{ fontSize: 'var(--font-12)' }}>Klarna</span>
      <ToggleSwitch scope="klarna-toggle" size="small" checked={klarnaEnabled} onChange={onToggleKlarna} />
    </div>
  </div>
);

const SubscriptionOverview = ({
  subscription,
  paymentMethods = [],
  klarnaEnabled = false,
  onToggleKlarna
}: {
  subscription: Subscription;
  paymentMethods?: string[];
  klarnaEnabled?: boolean;
  onToggleKlarna?: () => void;
}) => (
  <div className="stack-1">
    <div className={styles.header}>
      <strong>{subscription.planName}</strong>
      <SubscriptionStatusBadge subscription={subscription} />
    </div>

    <div className={styles.grid}>
      {subscription.amount != null && (
        <div>
          <div className={styles.label}>
            <Translate value={`${i18nBase}.amount`} />
          </div>
          <div>{formatAmount(subscription.amount)}</div>
        </div>
      )}
      {subscription.billingFrequency && (
        <div>
          <div className={styles.label}>
            <Translate value={`${i18nBase}.frequency`} />
          </div>
          <div>
            <Translate value={FREQUENCY_KEYS[subscription.billingFrequency] || subscription.billingFrequency} />
          </div>
        </div>
      )}
      {subscription.source && subscription.status !== 'ACTIVE' && (
        <div>
          <div className={styles.label}>Källa</div>
          <div>{SOURCE_LABELS[subscription.source] || subscription.source}</div>
        </div>
      )}
      {subscription.currentPeriodStart && subscription.currentPeriodEnd && (
        <div>
          <div className={styles.label}>
            <Translate value={`${i18nBase}.period`} />
          </div>
          <div>
            {subscription.currentPeriodStart} — {subscription.currentPeriodEnd}
          </div>
        </div>
      )}
      {subscription.activatedAt && (
        <div>
          <div className={styles.label}>
            <Translate value={`${i18nBase}.activated`} />
          </div>
          <div>{moment(subscription.activatedAt).format('YYYY-MM-DD HH:mm')}</div>
        </div>
      )}
      <div>
        <div className={styles.label}>
          <Translate value={`${i18nBase}.created`} />
        </div>
        <div>{moment(subscription.createdAt).format('YYYY-MM-DD HH:mm')}</div>
      </div>
      <div>
        <div className={styles.label}>
          <Translate value={`${i18nBase}.renewal`} />
        </div>
        <div>
          <Translate value={subscription.renewal ? `${i18nBase}.renewal_yes` : `${i18nBase}.renewal_no`} />
        </div>
      </div>
    </div>

    {subscription.status === 'PENDING' && subscription.source && PENDING_SOURCE_TEXT[subscription.source] && (
      <div className={styles.pendingNote}>{PENDING_SOURCE_TEXT[subscription.source]}</div>
    )}
    {subscription.status === 'PENDING' && !(subscription.source && PENDING_SOURCE_TEXT[subscription.source]) && (
      <div className={styles.pendingNote}>
        <Translate value={`${i18nBase}.pending_note`} />
      </div>
    )}

    {subscription.cancelledAt && (
      <div className={styles.grid}>
        <div>
          <div className={styles.label}>
            <Translate
              value={
                subscription.status === 'CANCELLED' ? `${i18nBase}.cancelled_at` : `${i18nBase}.renewal_cancelled_at`
              }
            />
          </div>
          <div>{moment(subscription.cancelledAt).format('YYYY-MM-DD HH:mm')}</div>
        </div>
        {subscription.cancelReason?.reason && (
          <div>
            <div className={styles.label}>
              <Translate value={`${i18nBase}.cancel_reason_label`} />
            </div>
            <div>
              {subscription.cancelReason.reason}
              {subscription.cancelReason.message ? `: ${subscription.cancelReason.message}` : ''}
            </div>
          </div>
        )}
      </div>
    )}

    {onToggleKlarna && (
      <PaymentMethodsRow methods={paymentMethods} klarnaEnabled={klarnaEnabled} onToggleKlarna={onToggleKlarna} />
    )}
  </div>
);

type PaymentHistoryProps = {
  payments: PatientPayment[];
  authToken: string;
  patientId: string | undefined;
  showNotification: (message: string, type: string) => void;
  paymentsSWRKey: string | null;
};

const PaymentHistory = ({ payments, authToken, patientId, showNotification, paymentsSWRKey }: PaymentHistoryProps) => {
  const [showAll, setShowAll] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [paymentToCancel, setPaymentToCancel] = useState<PatientPayment | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelPatientFee, setCancelPatientFee] = useState(true);
  const [cancelRegionSormlandReimbursement, setCancelRegionSormlandReimbursement] = useState(true);
  const [cancelCaregiverReimbursement, setCancelCaregiverReimbursement] = useState(true);
  const [cancelResponseMessage, setCancelResponseMessage] = useState('');
  const [cancelResponseSuccess, setCancelResponseSuccess] = useState('');

  const visiblePayments = showAll ? payments : payments.slice(0, VISIBLE_PAYMENTS_DEFAULT);

  const openCancelModal = (payment: PatientPayment) => {
    setPaymentToCancel(payment);
    setCancelModalVisible(true);
  };

  const closeCancelModal = () => {
    setCancelModalVisible(false);
    setPaymentToCancel(null);
    setCancelReason('');
    setCancelPatientFee(true);
    setCancelRegionSormlandReimbursement(true);
    setCancelCaregiverReimbursement(true);
    setCancelResponseMessage('');
    setCancelResponseSuccess('');
  };

  const submitCancelInvoice = () => {
    if (!paymentToCancel || !patientId) return;

    api
      .cancelInvoice(
        authToken,
        patientId,
        paymentToCancel.encounterId,
        cancelReason,
        cancelPatientFee,
        cancelRegionSormlandReimbursement,
        cancelCaregiverReimbursement
      )
      .then((res: Response) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((r: { success: boolean; messages?: { requiresUserAction: boolean; userMessage: string }[] }) => {
        if (r.success) {
          setCancelResponseSuccess('success');
          setCancelResponseMessage('Fakturan makulerad');
        } else {
          setCancelResponseSuccess('failed');
          setCancelResponseMessage(
            `Följande åtgärder kräver handpåläggning: \n\n \u2022 ${(r.messages || [])
              .filter((m) => m.requiresUserAction)
              .map((m) => m.userMessage)
              .join('\n \u2022 ')}`
          );
        }
        setCancelReason('');
        if (paymentsSWRKey) mutate(paymentsSWRKey);
      })
      .catch((err: unknown) => {
        showNotification('Något gick fel', 'error');
        console.error(err);
        closeCancelModal();
      });
  };

  const canCancelPayment = (payment: PatientPayment) =>
    payment.source === 'ENCOUNTER' &&
    payment.chargeSystem !== 'Insurance' &&
    payment.chargeSystem !== 'Subscription' &&
    payment.paymentProvider !== 'STRIPE' &&
    payment.paymentState !== 'CANCELLED';

  const [expandedPaymentId, setExpandedPaymentId] = useState<string | null>(null);

  const toggleExpand = (key: string) => {
    setExpandedPaymentId(expandedPaymentId === key ? null : key);
  };

  const [loadingReceiptId, setLoadingReceiptId] = useState<number | null>(null);

  // Paid subscription receipts are short-lived, so the list no longer carries the URL — fetch a
  // fresh one on demand when staff open the invoice.
  const openReceipt = async (payment: PatientPayment) => {
    if (!patientId || loadingReceiptId === payment.id) {
      return;
    }

    // Open the tab synchronously within the click gesture so it isn't blocked as a popup, then
    // redirect it once the short-lived URL is fetched. opener is nulled so the receipt page can't
    // script back into the admin window.
    const receiptWindow = window.open('', '_blank');
    if (receiptWindow) {
      receiptWindow.opener = null;
    }

    setLoadingReceiptId(payment.id);
    try {
      const res = await api.getInvoiceReceiptUrl(authToken, patientId, payment.id);
      if (res.status === 404) {
        showNotification('Ingen faktura tillgänglig för denna betalning', 'error');
        receiptWindow?.close();
        return;
      }
      if (!res.ok) {
        throw new Error();
      }
      const data = await res.json();
      if (data?.url) {
        if (receiptWindow) {
          receiptWindow.location.replace(data.url);
        } else {
          window.open(data.url, '_blank', 'noopener,noreferrer');
        }
      } else {
        showNotification('Ingen faktura tillgänglig för denna betalning', 'error');
        receiptWindow?.close();
      }
    } catch (err) {
      showNotification('Kunde inte hämta fakturan', 'error');
      console.error(err);
      receiptWindow?.close();
    } finally {
      setLoadingReceiptId(null);
    }
  };

  if (!payments.length) {
    return <span className="text-muted">Inga betalningar</span>;
  }

  return (
    <>
      <table className={`table is-fullwidth ${styles.table}`}>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Källa</th>
            <th>Belopp</th>
            <th>Status</th>
            <th>Åtgärd</th>
            <th style={{ width: '30px' }}></th>
          </tr>
        </thead>
        <tbody>
          {visiblePayments.map((payment) => {
            const rowKey = `${payment.source}-${payment.id}`;
            const isExpanded = expandedPaymentId === rowKey;
            // Provider + reference always render when expanded, so the row is always expandable.
            const hasDetails = true;

            return (
              <React.Fragment key={rowKey}>
                <tr
                  style={{ cursor: hasDetails ? 'pointer' : undefined }}
                  onClick={() => hasDetails && toggleExpand(rowKey)}
                >
                  <td>{payment.date || '-'}</td>
                  <td>{formatPaymentSource(payment)}</td>
                  <td>{payment.amount != null ? `${payment.amount} kr` : '-'}</td>
                  <td>{renderPaymentStatus(payment)}</td>
                  <td>
                    {canCancelPayment(payment) && (
                      <Button
                        buttonType="text"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          openCancelModal(payment);
                        }}
                      >
                        Makulera
                      </Button>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {hasDetails && (
                      <span className="text-button" style={{ fontSize: 'var(--font-12)', color: 'var(--grey-400)' }}>
                        {isExpanded ? '▲' : '▼'}
                      </span>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr key={`${rowKey}-details`} className={styles.detailRow}>
                    <td colSpan={6}>
                      <div className={styles.grid} style={{ padding: '5px 0' }}>
                        <div>
                          <div className={styles.label}>Betalleverantör</div>
                          <div>{formatPaymentProvider(payment.paymentProvider)}</div>
                        </div>
                        <div>
                          <div className={styles.label}>Referens</div>
                          <div>{payment.paymentReference || '-'}</div>
                        </div>
                        {payment.periodStart && payment.periodEnd && (
                          <div>
                            <div className={styles.label}>Period</div>
                            <div>
                              {payment.periodStart} — {payment.periodEnd}
                            </div>
                          </div>
                        )}
                        {payment.recipientUrl ? (
                          <div>
                            <div className={styles.label}>Faktura</div>
                            <div>
                              <a href={payment.recipientUrl} target="_blank" rel="noopener noreferrer">
                                Visa faktura
                              </a>
                            </div>
                          </div>
                        ) : (
                          payment.source === 'SUBSCRIPTION' &&
                          payment.status === 'COMPLETED' && (
                            <div>
                              <div className={styles.label}>Faktura</div>
                              <div>
                                <button
                                  type="button"
                                  className={`text-button ${styles.linkButton}`}
                                  disabled={loadingReceiptId === payment.id}
                                  onClick={() => openReceipt(payment)}
                                >
                                  {loadingReceiptId === payment.id ? 'Hämtar…' : 'Visa faktura'}
                                </button>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {payments.length > VISIBLE_PAYMENTS_DEFAULT && (
        <div className="text-center mt-10">
          <span className="text-button" onClick={() => setShowAll(!showAll)}>
            <Translate value={`global.show_${showAll ? 'fewer' : 'all'}`} />
          </span>
        </div>
      )}

      <Modal
        visible={cancelModalVisible}
        actionCompletable={
          cancelReason !== '' && (cancelPatientFee || cancelRegionSormlandReimbursement || cancelCaregiverReimbursement)
        }
        size="medium"
        headerI18nKey="member_details.payment_history.cancel_invoice_modal.header"
        actionI18nKey="member_details.payment_history.cancel_invoice_modal.button_action"
        onClose={closeCancelModal}
        onActionCompleted={submitCancelInvoice}
        allowEnterCompletion={false}
      >
        <div className="stack">
          <div className="checkbox-container">
            <input
              type="checkbox"
              id="cancel-patientfee"
              checked={cancelPatientFee}
              onChange={() => setCancelPatientFee(!cancelPatientFee)}
            />
            <label htmlFor="cancel-patientfee" className="ml-5 fw-500">
              <Translate value="member_details.payment_history.cancel_invoice_modal.options.patient_fee_title" />
              <div className="checkbox-description">
                <Translate value="member_details.payment_history.cancel_invoice_modal.options.patient_fee_description" />
              </div>
            </label>
            {paymentToCancel?.chargeSystem === 'RegionSormland' && (
              <>
                <input
                  type="checkbox"
                  id="cancel-region-sormland"
                  checked={cancelRegionSormlandReimbursement}
                  onChange={() => setCancelRegionSormlandReimbursement(!cancelRegionSormlandReimbursement)}
                />
                <label htmlFor="cancel-region-sormland" className="ml-5 fw-500">
                  <Translate value="member_details.payment_history.cancel_invoice_modal.options.region_sormland_title" />
                  <div className="checkbox-description">
                    <Translate value="member_details.payment_history.cancel_invoice_modal.options.region_sormland_description" />
                  </div>
                </label>
              </>
            )}
            <input
              type="checkbox"
              id="cancel-caregiver-reimbursement"
              checked={cancelCaregiverReimbursement}
              onChange={() => setCancelCaregiverReimbursement(!cancelCaregiverReimbursement)}
            />
            <label htmlFor="cancel-caregiver-reimbursement" className="ml-5 fw-500">
              <Translate value="member_details.payment_history.cancel_invoice_modal.options.caregiver_reimbursement_title" />
              <div className="checkbox-description">
                <Translate value="member_details.payment_history.cancel_invoice_modal.options.caregiver_reimbursement_description" />
              </div>
            </label>
            <div className="mt-10">
              <label htmlFor="cancel-reason">
                <Translate value="member_details.payment_history.cancel_invoice_modal.reason" />
              </label>
              <input
                className="mt-5"
                id="cancel-reason"
                type="text"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
            {cancelResponseMessage && (
              <div className={`mt-15 cancel-invoice-response__${cancelResponseSuccess}`}>
                <RenderNewLines text={cancelResponseMessage} />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = (state: FixMe) => ({
  patient: state.members.currentMember,
  authority: state.sharedData.authorityTypes,
  authToken: state.auth.token.jwt
});

const mapActionsToProps = {
  showNotification
};

export const ConnectedSubscriptionInfo = connect(mapStateToProps, mapActionsToProps)(SubscriptionInfoSection);
export const ConnectedPaymentHistory = connect(mapStateToProps, mapActionsToProps)(PaymentHistorySection);

export default ConnectedSubscriptionInfo;
